import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import { PhoneFlowHub } from './PhoneFlowHub';

/** Props */
interface PhoneFlowModalProps {
  locale: Locale;
  startFromConfirm?: boolean;
  successSavePhoneCallback?: (response?: ApiSchema<'PhoneNumberInfo'>) => void;
  successConfirmPhoneCallback?: () => void;
}

/**
 * @description Renders the phone flow modal. Handles the phone asking and confirmation steps.
 * - `locale` - The locale.
 * - `startFromConfirm` - Start from the phone confirm step.
 * - `successSavePhoneCallback` - The success callback for saving the phone number.
 * - `successConfirmPhoneCallback` - The success callback for confirming the phone number.
 */
export const PhoneFlowModal = NiceModal.create((props: PhoneFlowModalProps) => {
  // Destructure props
  const {
    locale,
    startFromConfirm,
    successSavePhoneCallback,
    successConfirmPhoneCallback,
  } = props;

  // Use a hook to manage the modal state
  const modal = useModal();

  // Close modal
  const closeModal = () => {
    void modal.hide();
  };

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // *************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="sm"
      backdropClickDisabled
      header={startFromConfirm ? t('confirm.phone.user_profile.button') : t('global.placeholder.add-phone')}
      body={(
        <PhoneFlowHub
          locale={locale}
          startFromConfirm={startFromConfirm}
          successSavePhoneCallback={successSavePhoneCallback}
          successConfirmPhoneCallback={successConfirmPhoneCallback}
        />
      )}
    />
  );
});

/**
 * @description Opens the phone flow modal anywhere in the app. The modal handles the phone asking
 * and confirmation steps.
 * - `locale` - The locale.
 * - `startFromConfirm` - Start from the phone confirm step.
 * - `successSavePhoneCallback` - The success callback for saving the phone number.
 * - `successConfirmPhoneCallback` - The success callback for confirming the phone number.
 *
 * @example
 * import { showPhoneFlowModal } from '@components/features/modals/phone/PhoneFlowModal';
 * // usage
 * <Button onClick={() => showPhoneFlowModal({ locale, startFromConfirm: true })}>
 *   Confirm phone
 * </Button>
 */
export const showPhoneFlowModal = (props: PhoneFlowModalProps) => {
  void NiceModal.show(PhoneFlowModal, props);
};

/**
 * @description Closes the phone flow modal.
 */
export const closePhoneFlowModal = () => {
  void NiceModal.hide(PhoneFlowModal);
};
